"use client";

/** @jsxImportSource @emotion/react */
import { type Interpolation } from "@emotion/react";
import type { CommunityActivityFeedResult } from "@packages/sdk";
import { communityActivityFeedReferenceTypeEnum } from "@packages/sdk";
import { Avatar, Skeleton, spacing } from "@packages/ui";
import type { HTMLAttributes } from "react";
import { forwardRef } from "react";

import {
  ActivityCardIntention,
  ActivityCardPromptResponse,
} from "./activityCards";

const baseStyles: Interpolation = {
  display: "flex",
  flexDirection: "column",
  gap: spacing.ms,
};

const cardStyles: Interpolation = {
  paddingBottom: spacing.full,
  width: spacing.full,
};

const headerStyles: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.s,
};

const headerTextStyles: Interpolation = {
  display: "flex",
  flex: 1,
  flexDirection: "column",
  gap: spacing.t,
};

const headerTextNameStyles: Interpolation = {
  height: 18,
  width: "75%",
};

const headerTextTimeStyles: Interpolation = {
  height: spacing.m,
  width: "25%",
};

type ActivityCardProps = Omit<HTMLAttributes<HTMLDivElement>, "children"> & {
  data: CommunityActivityFeedResult;
  loading?: boolean;
};

export const ActivityCard = forwardRef<HTMLDivElement, ActivityCardProps>(
  ({ data, loading = false, ...props }, ref) => {
    if (!data || loading)
      return (
        <div ref={ref} css={baseStyles} {...props}>
          <div css={headerStyles}>
            <Avatar loading size={40} />
            <div css={headerTextStyles}>
              <Skeleton css={headerTextNameStyles} />
              <Skeleton css={headerTextTimeStyles} />
            </div>
          </div>
          <Skeleton css={cardStyles} />
        </div>
      );

    switch (data?.reference_type) {
      case communityActivityFeedReferenceTypeEnum.enum.intention:
        return <ActivityCardIntention data={data} ref={ref} {...props} />;
      case communityActivityFeedReferenceTypeEnum.enum.prompt_response:
        return <ActivityCardPromptResponse data={data} ref={ref} {...props} />;
      default:
        return null;
    }
  },
);

ActivityCard.displayName = "ActivityCard";
